import React, { useState, useEffect, useRef } from 'react';
import useSupabaseClient from '../hooks/useSupabaseClient';
import { Box, Typography, Button, TextField, Table, TableBody, TableCell, TableHead, TableRow, Paper } from '@mui/material';

const InvoiceAdvancesSection = ({ invoiceId, cxpEntry, onApplyAdvance, refreshTrigger, enabled }) => {
  const { supabase } = useSupabaseClient();
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applyAmounts, setApplyAmounts] = useState({});
  const [applying, setApplying] = useState(false);
  const applyingRef = useRef(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (invoiceId && enabled) {
      fetchAdvances();
    }
  }, [invoiceId, refreshTrigger, enabled]);

  const fetchAdvances = async () => {
    setLoading(true);
    setError('');
    try {
      const storedUser = localStorage.getItem('invoice_user');
      if (!storedUser) return;
      const user = JSON.parse(storedUser);

      // 1. Get the invoice to find the supplier NIT
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .select('num_identificacion')
        .eq('id', invoiceId)
        .single();

      if (invoiceError || !invoice) throw invoiceError || new Error('Factura no encontrada');

      // 2. Fetch advances for this supplier
      const nitStr = invoice.num_identificacion ? invoice.num_identificacion.toString().trim() : '';
      let nitSearch = nitStr;
      if (nitStr.includes('-')) {
        nitSearch = nitStr.split('-')[0].trim();
      }

      const { data: advancesData, error: advancesError } = await supabase
        .from('invoice_supplier_advances')
        .select('*')
        .eq('tenant_user_id', user.id)
        .eq('supplier_nit', nitSearch)
        .not('erp_balance', 'is', null)
        .gt('available_balance', 0); // Solo traer anticipos con saldo mayor a 0

      if (advancesError) throw advancesError;

      setAdvances(advancesData || []);
    } catch (err) {
      console.error('Error fetching advances:', err);
      setAdvances([]);
      setError('No se pudieron consultar los anticipos: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAmountChange = (id, value) => {
    setApplyAmounts(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleApply = async (advance) => {
    if (applyingRef.current) return;
    const amountStr = applyAmounts[advance.id] || advance.available_balance.toString();
    const amount = parseFloat(amountStr);

    if (isNaN(amount) || amount <= 0) {
      alert('Monto inválido');
      return;
    }

    if (amount > advance.available_balance) {
      alert(`El monto máximo a aplicar es ${advance.available_balance}`);
      return;
    }

    if (!cxpEntry) {
      alert('No se detectó una cuenta CxP en la causación. Asegúrese de que exista una cuenta (ej: 2205/2335) con saldo crédito.');
      return;
    }

    if (amount > cxpEntry.credit) {
      alert(`El monto supera el valor de la CxP actual (${cxpEntry.credit})`);
      return;
    }

    applyingRef.current = true;
    setApplying(true);
    try {
      await onApplyAdvance(advance, amount);
      await fetchAdvances();
    } finally {
      applyingRef.current = false;
      setApplying(false);
    }
    
    // Clear amount
    setApplyAmounts(prev => ({
      ...prev,
      [advance.id]: ''
    }));
  };

  if (!enabled) return null; // No renderizar nada si la configuración está deshabilitada
  if (error) return <Typography color="error" role="alert">{error}</Typography>;
  if (loading) return null; // Retornar null para evitar saltos visuales feos mientras carga
  if (advances.length === 0) return null; // No mostrar nada si no hay anticipos

  return (
    <Box sx={{ mb: 2, p: { xs: 1, sm: 2 }, border: '1px dashed #1976d2', borderRadius: 2, bgcolor: '#f0f7ff' }}>
      <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
        ?? Anticipos Disponibles
      </Typography>
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          '@media (max-height: 900px)': { maxHeight: 190 }
        }}
      >
        <Table stickyHeader size="small" sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>Cuenta</TableCell>
              <TableCell>Cruce/INVC</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell align="right">Saldo</TableCell>
              <TableCell align="center">Aplicar</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {advances.map(adv => (
              <TableRow key={adv.id}>
                <TableCell>{adv.acct}</TableCell>
                <TableCell>{adv.cruce} - {adv.invc}</TableCell>
                <TableCell>{adv.advance_date ? new Date(adv.advance_date).toLocaleDateString() : 'N/A'}</TableCell>
                <TableCell align="right">
                  {Number(adv.available_balance).toLocaleString('es-CO', { style: 'currency', currency: 'COP' })}
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 210 }}>
                    <TextField
                      size="small"
                      placeholder={adv.available_balance.toString()}
                      value={applyAmounts[adv.id] || ''}
                      onChange={(e) => handleAmountChange(adv.id, e.target.value)}
                      sx={{ width: 120, bgcolor: 'white' }}
                      type="number"
                      inputProps={{ step: "0.01", max: adv.available_balance }}
                    />
                    <Button 
                      variant="contained" 
                      color="primary" 
                      size="small"
                      onClick={() => handleApply(adv)}
                      disabled={applying}
                    >
                      Cruzar
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default InvoiceAdvancesSection;
